import React, { useEffect, useState } from "react";
import { useApp } from "@/context/AppContext";
import { useToast } from "@/hooks/use-toast";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import LocationPicker from "@/components/common/LocationPicker";
import { countyOf } from "@/data/romania";
import { myListings, markSold, deleteListing as apiDeleteListing } from "@/lib/api";
import { Link, useNavigate } from "react-router-dom";
import { User, LogOut, Phone, MapPin, Edit2, Trash2, CheckCircle2, Plus, Clock, Tag, Eye } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

export default function Profile() {
  const { user, updateProfile, logout } = useApp();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", phone: "", county: "", city: "" });
  const [saving, setSaving] = useState(false);
  const [items, setItems] = useState([]);
  const [loadingItems, setLoadingItems] = useState(true);

  useEffect(() => {
    if (user) setForm({ name: user.name || "", phone: user.phone || "", county: user.county || countyOf(user.city) || "", city: user.city || "" });
  }, [user]);

  const reload = async () => {
    setLoadingItems(true);
    try {
      const d = await myListings();
      setItems(d.items || []);
    } finally { setLoadingItems(false); }
  };
  useEffect(() => { reload(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await updateProfile(form);
      toast({ title: "Profil actualizat" });
    } catch (err) {
      toast({ title: "Eroare", description: err?.response?.data?.detail || "Nu am putut salva." });
    } finally { setSaving(false); }
  };

  const onSold = async (id) => {
    if (!confirm("Marchezi anunțul ca vândut?")) return;
    try { await markSold(id); toast({ title: "Anunț marcat ca vândut" }); await reload(); }
    catch (err) { toast({ title: "Eroare", description: err?.response?.data?.detail || "" }); }
  };
  const onDelete = async (id) => {
    if (!confirm("Șterge anunțul definitiv?")) return;
    try { await apiDeleteListing(id); toast({ title: "Anunț șters" }); await reload(); }
    catch (err) { toast({ title: "Eroare", description: err?.response?.data?.detail || "" }); }
  };
  const onLogout = async () => { await logout(); navigate("/"); };

  if (!user) return null;

  const grouped = {
    all: items,
    pending: items.filter((x) => x.status === "pending"),
    active: items.filter((x) => x.status === "active"),
    sold: items.filter((x) => x.status === "sold"),
    rejected: items.filter((x) => x.status === "rejected"),
  };

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-6 py-6 md:py-8 fade-in">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-14 h-14 rounded-full grid place-items-center text-white font-black text-xl" style={{ background: "var(--amd-primary)" }}>
          {user.picture ? <img src={user.picture} alt="" className="w-14 h-14 rounded-full object-cover" /> : (user.name || "?").charAt(0).toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl md:text-3xl font-black text-white truncate">{user.name}</h1>
          <p className="text-sm" style={{ color: "var(--amd-text-muted)" }}>{user.email} {user.auth_provider === "google" ? "• Google" : "• Email"}</p>
        </div>
        <Button onClick={onLogout} variant="outline" className="border-white/20 text-white hover:bg-white/5 gap-2">
          <LogOut className="w-4 h-4" /> Ieșire
        </Button>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1 amd-card rounded-lg p-5 h-fit">
          <h2 className="font-bold text-white mb-3 flex items-center gap-2"><User className="w-4 h-4" /> Detalii cont</h2>
          <form onSubmit={save} className="space-y-3">
            <div>
              <Label className="text-white text-xs uppercase tracking-wider">Nume</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="amd-input h-10 mt-1" required />
            </div>
            <div>
              <Label className="text-white text-xs uppercase tracking-wider">Telefon</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="amd-input h-10 mt-1" />
            </div>
            <div>
              <Label className="text-white text-xs uppercase tracking-wider">Județ & Localitate</Label>
              <div className="mt-1">
                <LocationPicker
                  county={form.county}
                  city={form.city}
                  onChange={(county, city) => setForm((f) => ({ ...f, county, city }))}
                  className="grid grid-cols-1 gap-2"
                  testIdPrefix="profile"
                />
              </div>
            </div>
            <Button type="submit" disabled={saving} className="amd-btn-primary w-full h-10 font-semibold">{saving ? "Se salvează…" : "Salvează modificările"}</Button>
          </form>
        </div>

        <div className="lg:col-span-2">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-bold text-white text-lg flex items-center gap-2"><Tag className="w-5 h-5" /> Anunțurile mele ({items.length})</h2>
            <Button onClick={() => navigate("/adauga-anunt")} className="amd-btn-primary gap-2 h-9"><Plus className="w-4 h-4" /> Anunț nou</Button>
          </div>
          <Tabs defaultValue="all">
            <TabsList className="bg-transparent border" style={{ borderColor: "var(--amd-border)" }}>
              <TabsTrigger value="all">Toate ({grouped.all.length})</TabsTrigger>
              <TabsTrigger value="pending">În verificare ({grouped.pending.length})</TabsTrigger>
              <TabsTrigger value="active">Active ({grouped.active.length})</TabsTrigger>
              <TabsTrigger value="sold">Vândute ({grouped.sold.length})</TabsTrigger>
              <TabsTrigger value="rejected">Respinse ({grouped.rejected.length})</TabsTrigger>
            </TabsList>
            {["all", "pending", "active", "sold", "rejected"].map((k) => (
              <TabsContent key={k} value={k} className="mt-3 space-y-2">
                {loadingItems ? <div className="text-sm" style={{ color: "var(--amd-text-muted)" }}>Se încarcă…</div> :
                  grouped[k].length === 0 ? (
                    <div className="amd-card rounded-lg p-8 text-center text-sm" style={{ color: "var(--amd-text-muted)" }}>Niciun anunț aici.</div>
                  ) : grouped[k].map((l) => (
                    <div key={l.id} className="amd-card rounded-lg p-3 flex flex-col sm:flex-row gap-3">
                      <Link to={`/anunt/${l.id}`} className="shrink-0">
                        <img src={l.images?.[0]} alt={l.title} className="w-full sm:w-28 h-28 sm:h-20 rounded object-cover" />
                      </Link>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Link to={`/anunt/${l.id}`} className="font-bold text-white hover:underline truncate">{l.title}</Link>
                          <StatusBadge status={l.status} />
                        </div>
                        <div className="font-black" style={{ color: "var(--amd-primary)" }}>{Number(l.price).toLocaleString("ro-RO")} {l.currency}</div>
                        <div className="text-xs flex flex-wrap gap-3 mt-1" style={{ color: "var(--amd-text-muted)" }}>
                          <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {l.location || l.city}</span>
                          <span className="inline-flex items-center gap-1"><Eye className="w-3 h-3" /> {l.views || 0}</span>
                          <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {new Date(l.created_at).toLocaleDateString("ro-RO")}</span>
                        </div>
                      </div>
                      <div className="flex sm:flex-col gap-2 shrink-0">
                        <Button onClick={() => navigate(`/anunt/${l.id}/editare`)} variant="outline" className="border-white/20 text-white hover:bg-white/5 h-9 px-3 gap-1"><Edit2 className="w-4 h-4" /></Button>
                        {l.status !== "sold" && <Button onClick={() => onSold(l.id)} className="amd-btn-primary h-9 px-3 gap-1"><CheckCircle2 className="w-4 h-4" /></Button>}
                        <Button onClick={() => onDelete(l.id)} variant="outline" className="border-white/20 text-white hover:bg-white/5 h-9 px-3 gap-1"><Trash2 className="w-4 h-4" /></Button>
                      </div>
                    </div>
                  ))
                }
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    pending: { label: "În verificare", bg: "#f59e0b", color: "#000" },
    active: { label: "Activ", bg: "var(--amd-primary)", color: "#fff" },
    sold: { label: "Vândut", bg: "#16A34A", color: "#fff" },
    rejected: { label: "Respins", bg: "#444", color: "#fff" },
  };
  const s = map[status] || map.pending;
  return <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded" style={{ background: s.bg, color: s.color }}>{s.label}</span>;
}
